// Solemate motion: smooth accordions, review wall (filters + show more), survey bars, timeline,
// scroll reveal, smooth scroll to the buy box and the socks add-on. Loaded as a module, so it runs once
// however many blocks include it. Every animation respects "reduce motion".
import { CartLinesUpdateEvent } from '@shopify/events';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const still = () => reduce.matches;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';

/* ---------------------------------------------------------------- accordions */
// One animator for the Solemate FAQ cards and the condition picker. Animates the panel's height,
// fade and a small slide, in every browser (the CSS-only route only animates in Chrome).
const ACC = 'details.sm-faq__item, details.sm-cond__item';

function panelOf(details) {
  return details.querySelector(':scope > summary + *');
}

function openAcc(details) {
  const panel = panelOf(details);
  details.classList.add('is-open');
  details.open = true;
  if (!panel || still()) return;
  panel.getAnimations().forEach((a) => a.cancel());
  const h = panel.scrollHeight;
  panel.style.overflow = 'hidden';
  const anim = panel.animate(
    [
      { height: '0px', opacity: 0, transform: 'translateY(-6px)' },
      { height: h + 'px', opacity: 1, transform: 'none' },
    ],
    { duration: Math.min(560, 300 + h * 0.35), easing: EASE_OUT }
  );
  anim.onfinish = anim.oncancel = () => (panel.style.overflow = '');
}

function closeAcc(details) {
  const panel = panelOf(details);
  details.classList.remove('is-open');
  if (!panel || still()) {
    details.open = false;
    return;
  }
  panel.getAnimations().forEach((a) => a.cancel());
  const h = panel.offsetHeight;
  panel.style.overflow = 'hidden';
  const anim = panel.animate(
    [
      { height: h + 'px', opacity: 1, transform: 'none' },
      { height: '0px', opacity: 0, transform: 'translateY(-4px)' },
    ],
    { duration: Math.min(420, 240 + h * 0.25), easing: EASE_IN_OUT }
  );
  anim.onfinish = () => {
    details.open = false;
    panel.style.overflow = '';
  };
}

function setupAccordions(root = document) {
  root.querySelectorAll(ACC).forEach((d) => {
    if (d.dataset.smAcc || d.closest('[data-sm-tabs]')) return;
    d.dataset.smAcc = '1';
    // Handle exclusive groups ourselves, so the ones closing animate too.
    if (d.getAttribute('name')) {
      d.dataset.smGroup = d.getAttribute('name');
      d.removeAttribute('name');
    }
    if (d.open) d.classList.add('is-open');
    const summary = d.querySelector(':scope > summary');
    summary?.addEventListener('click', (e) => {
      e.preventDefault();
      if (d.classList.contains('is-open')) {
        closeAcc(d);
      } else {
        if (d.dataset.smGroup) {
          document.querySelectorAll(`details[data-sm-group="${CSS.escape(d.dataset.smGroup)}"].is-open`).forEach((o) => o !== d && closeAcc(o));
        }
        openAcc(d);
      }
    });
  });
}

/* ---------------------------------------------------------------- condition chips + card */
function setupTabs(box) {
  if (box.dataset.smReady) return;
  const list = box.querySelector('.sm-cond__list');
  const items = [...list.querySelectorAll(':scope > details, :scope > * > details')].filter((d) => d.matches('.sm-cond__item'));
  if (items.length < 2) return;
  box.dataset.smReady = '1';
  const chips = document.createElement('div');
  chips.className = 'sm-cond__chips';
  chips.setAttribute('role', 'tablist');
  chips.setAttribute('aria-label', 'Choose what describes your feet');
  let current = Math.max(0, items.findIndex((d) => d.open));
  const uid = Math.random().toString(36).slice(2, 7);
  const buttons = items.map((d, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sm-cond__chip';
    b.setAttribute('role', 'tab');
    b.id = `smc-${uid}-${i}`;
    b.textContent = d.querySelector('summary')?.textContent.trim() || `Option ${i + 1}`;
    d.setAttribute('role', 'tabpanel');
    d.setAttribute('aria-labelledby', b.id);
    d.open = true;
    d.removeAttribute('name');
    b.addEventListener('click', () => {
      show(i);
      b.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    });
    b.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
      buttons[n].focus();
      show(n);
    });
    chips.append(b);
    return b;
  });
  list.before(chips);
  list.classList.add('is-tabs');
  const paint = () => {
    items.forEach((d, i) => (d.hidden = i !== current));
    buttons.forEach((b, i) => {
      b.setAttribute('aria-selected', String(i === current));
      b.tabIndex = i === current ? 0 : -1;
    });
  };
  function show(i) {
    if (i === current) return;
    if (still()) {
      current = i;
      paint();
      return;
    }
    const before = list.offsetHeight;
    current = i;
    paint();
    const after = list.offsetHeight;
    list.animate([{ height: before + 'px' }, { height: after + 'px' }], { duration: 420, easing: EASE_OUT });
    list.style.overflow = 'hidden';
    setTimeout(() => (list.style.overflow = ''), 440);
    items[i].animate(
      [
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 460, easing: EASE_OUT }
    );
  }
  paint();
}

/* ---------------------------------------------------------------- review wall */
function setupWall(wall) {
  if (wall.dataset.smReady) return;
  wall.dataset.smReady = '1';
  const grid = wall.querySelector('[data-sm-grid]');
  const clip = wall.querySelector('[data-sm-clip]');
  const filters = wall.querySelector('[data-sm-filters]');
  const moreWrap = wall.querySelector('[data-sm-more-wrap]');
  const more = wall.querySelector('[data-sm-more]');
  const label = wall.querySelector('[data-sm-more-label]');
  const moreText = label?.textContent || 'Show more reviews';
  const cards = [...grid.querySelectorAll('.sm-rv')];
  if (!cards.length) return;

  const mobile = window.matchMedia('(max-width: 749px)');
  const initial = () => Number(mobile.matches ? wall.dataset.initialMobile : wall.dataset.initial) || 6;
  let tag = 'All';
  let expanded = false;

  const matching = () => cards.filter((c) => tag === 'All' || c.dataset.tag === tag);

  function apply() {
    const list = matching();
    const limit = expanded ? list.length : initial();
    cards.forEach((c) => (c.hidden = true));
    list.forEach((c, i) => (c.hidden = i >= limit));
    const hasMore = list.length > initial();
    moreWrap.hidden = !hasMore;
    more.setAttribute('aria-expanded', String(expanded));
    if (label) label.textContent = expanded ? 'Show fewer reviews' : moreText;
  }

  // Animate the wall's height between two layouts, and fade newly shown cards up one by one.
  function morph(change, { scrollBack = false } = {}) {
    if (still()) {
      change();
      return;
    }
    const before = clip.offsetHeight;
    const wasVisible = new Set(cards.filter((c) => !c.hidden));
    change();
    const after = clip.offsetHeight;
    clip.getAnimations().forEach((a) => a.cancel());
    clip.animate([{ height: before + 'px' }, { height: after + 'px' }], {
      duration: Math.min(900, 380 + Math.abs(after - before) * 0.25),
      easing: EASE_OUT,
    });
    let n = 0;
    cards.forEach((c) => {
      if (!c.hidden && !wasVisible.has(c)) {
        c.animate(
          [
            { opacity: 0, transform: 'translateY(16px) scale(0.985)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 560, delay: Math.min(n++ * 55, 500), easing: EASE_OUT, fill: 'backwards' }
        );
      }
    });
    if (scrollBack) wall.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Topic chips, built from the tags the reviews actually have.
  const tags = [...new Set(cards.map((c) => c.dataset.tag).filter(Boolean))];
  if (tags.length > 1) {
    ['All', ...tags].forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sm-wall__chip';
      b.textContent = t;
      b.setAttribute('aria-pressed', String(t === 'All'));
      b.addEventListener('click', () => {
        if (t === tag) return;
        filters.querySelectorAll('.sm-wall__chip').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        morph(() => {
          tag = t;
          expanded = false;
          apply();
        });
      });
      filters.append(b);
    });
    filters.hidden = false;
  }

  more.addEventListener('click', () => {
    const collapsing = expanded;
    morph(
      () => {
        expanded = !expanded;
        apply();
      },
      { scrollBack: collapsing }
    );
  });

  mobile.addEventListener('change', apply);
  apply();
}

/* ---------------------------------------------------------------- on-scroll effects */
const groups = [
  { sel: '.sm-how__copy > *, .sm-how__media, .sm-day__head, .sm-sci__head, .sm-sci__row > *, .sm-sci__auth, .sm-sci__check, .sm-survey__head, .sm-wall__head, .sm-socks, .sm-close' },
  { sel: '.sm-mech, .sm-cond__title, .sm-quotes__title, .sm-steps, .sm-rating, .sm-compare, .sm-support, .sm-faq__intro' },
  { sel: '.sm-how__step', stagger: 0.1 },
  { sel: '.sm-stop', stagger: 0.12 },
  { sel: '.sm-sci__fam-item, .sm-sci__does-item', stagger: 0.09 },
  { sel: '.sm-sstat', stagger: 0.09 },
  { sel: '.sm-faq__item', stagger: 0.05 },
  { sel: '.sm-cond__item', stagger: 0.05 },
  { sel: '.sm-quote, .sm-testimonial', stagger: 0.09 },
  { sel: '.sm-steps__item', stagger: 0.12 },
  { sel: '.sm-finder, .sm-range__head, .sm-kit, .sm-band, .sm-stats > .sm-heading, .sm-expert, .sm-story, .sm-email, .sm-guarantee' },
  { sel: '.sm-card-shoe', stagger: 0.08 },
  { sel: '.sm-stat', stagger: 0.09 },
];

function countUp(el) {
  const target = Number(el.textContent);
  if (!target || still()) return;
  const start = performance.now();
  const dur = 1100;
  const step = (t) => {
    const p = Math.min(1, (t - start) / dur);
    el.textContent = String(Math.round(target * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(step);
  };
  el.textContent = '0';
  requestAnimationFrame(step);
}

function setupScroll() {
  if (!('IntersectionObserver' in window) || still()) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const el = e.target;
        el.classList.add('sm-revealed');
        if (el.matches('.sm-sstat')) {
          el.classList.add('sm-fill');
          const n = el.querySelector('[data-sm-count]');
          if (n) countUp(n);
        }
        if (el.matches('.sm-day__list')) el.classList.add('sm-draw');
        io.unobserve(el);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  const fold = window.innerHeight * 0.9;
  groups.forEach((g) => {
    document.querySelectorAll(g.sel).forEach((el, i) => {
      if (el.dataset.smRev) return;
      el.dataset.smRev = '1';
      if (el.getBoundingClientRect().top < fold) {
        if (el.matches('.sm-sstat')) el.classList.add('sm-fill');
        return; // already on screen: never hide content the reader can see
      }
      if (g.stagger) el.style.setProperty('--sm-delay', ((i % 4) * g.stagger).toFixed(2) + 's');
      el.classList.add('sm-will-reveal');
      io.observe(el);
    });
  });
  document.querySelectorAll('.sm-sstat, .sm-day__list').forEach((el) => {
    if (el.getBoundingClientRect().top < fold) {
      el.classList.add('sm-fill', 'sm-draw');
      return;
    }
    el.classList.add('sm-armed');
    io.observe(el);
  });
}

/* ---------------------------------------------------------------- smooth scroll to the buy box */
// The size picker in the main buy box (not the sticky bar): where every "Choose your size" link lands.
function findSize() {
  const box = document.querySelector('.product-information') || document;
  const sets = [...box.querySelectorAll('variant-picker fieldset, .variant-picker fieldset')].filter((f) => f.offsetParent);
  return sets.find((f) => /^\s*size/i.test(f.querySelector('legend')?.textContent || '')) || sets[0] || null;
}
document.addEventListener('click', (e) => {
  const link = e.target.closest('[data-sm-to-buy]');
  if (!link) return;
  e.preventDefault();
  const size = findSize();
  (size || document.querySelector('.product-information'))?.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'center' });
  // Draw the eye to the size choice, the one thing left to pick before adding to the bag.
  if (size && !still()) setTimeout(() => {
    size.classList.remove('sm-pulse');
    void size.offsetWidth;
    size.classList.add('sm-pulse');
  }, 650);
});

/* ---------------------------------------------------------------- extra "Add to bag" buttons */
// They press the main button, so the size and kit chosen in the buy box are what's added.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sm-add-main]');
  if (!btn) return;
  e.preventDefault();
  const main = document.querySelector('.product-details add-to-cart-component button[name="add"]');
  if (main && !main.disabled) main.click();
  else (findSize() || document.querySelector('.product-information'))?.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'center' });
});

/* ---------------------------------------------------------------- socks add-on */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-sm-add]');
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  btn.setAttribute('aria-busy', 'true');
  const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.dataset.smAdd), quantity: 1 }], sections }),
    });
    if (!res.ok) throw new Error('add failed');
    const result = await res.json();
    const cart = await (await fetch('/cart.js')).json();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [], promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'solemate-socks', itemCount: cart.item_count, sections: result.sections, didError: false },
    });
    // Slide the cart drawer open so the shopper sees the socks go in.
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (drawer && !drawer.isOpen && typeof drawer.open === 'function') setTimeout(() => drawer.open(), 60);
    else if (!drawer) document.querySelector('a[href="/cart"], [aria-controls*="cart" i]')?.click();
    btn.classList.add('is-added');
    setTimeout(() => btn.classList.remove('is-added'), 2200);
  } catch (err) {
    location.href = '/cart';
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------- price on the Add to bag button */
// Reads the live price from the buy-box header (re-rendered by Horizon when the kit or size changes)
// and writes "Add to bag · £85" into the main button. Re-applies after every re-render.
function priceOnButton() {
  const area = document.querySelector('.product-information');
  if (!area) return;
  const apply = () => {
    const priceEl = area.querySelector('.product-details .sm-header__price') || area.querySelector('.sm-header__price');
    const btn = area.querySelector('.product-details add-to-cart-component button[name="add"]');
    if (!priceEl || !btn || btn.disabled) return; // keep "Sold out" / "Unavailable" untouched
    const node = [...btn.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim());
    if (!node) return;
    const price = priceEl.textContent.trim().replace(/^From\s+/i, '').replace(/\.00$/, '');
    const base = node.nodeValue.split('·')[0].trim();
    const text = ` ${base} · ${price} `;
    if (node.nodeValue !== text) node.nodeValue = text;
    // Sticky bar: price inside the button (shown on phones only, where the separate price is hidden)
    const sticky = document.querySelector('.sticky-add-to-cart__button:not([disabled]) .add-to-cart-text__content');
    if (sticky) {
      let tag = sticky.querySelector('.sm-sticky-price');
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'sm-sticky-price';
        sticky.firstElementChild?.after(tag);
      }
      if (tag.textContent !== ' · ' + price) tag.textContent = ' · ' + price;
    }
    document.querySelectorAll('[data-sm-cta-text]').forEach((el) => {
      const t = el.textContent.split('·')[0].trim() + ' · ' + price;
      if (el.textContent !== t) el.textContent = t;
    });
  };
  // The buy-box header price is rendered once on load, so set it from the selected variant whenever the kit,
  // colour or size changes (and the free-socks chip: 7 pairs per pair of shoes, so 14 for the Out + Home Kit).
  const handle = (location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
  let product = null;
  const syncPrice = async () => {
    if (!handle) return;
    try { product = product || (await fetch(`/products/${handle}.js`).then((r) => r.json())); } catch (e) { return; }
    const id = area.querySelector('form[action*="/cart/add"] input[name="id"]')?.value || new URLSearchParams(location.search).get('variant');
    const v = product.variants.find((x) => String(x.id) === String(id));
    if (!v) return;
    const money = '£' + (v.price / 100).toFixed(2);
    document.querySelectorAll('.sm-header__price').forEach((el) => { if (el.textContent.trim() !== money) el.textContent = money; });
    const pairs = /out \+ home/i.test(v.title) ? 14 : 7;
    document.querySelectorAll('.sm-header .sm-chip').forEach((el) => {
      const t = el.textContent.replace(/\b(7|14) pairs/, pairs + ' pairs');
      if (t !== el.textContent) el.textContent = t;
    });
    apply();
  };
  document.addEventListener('change', (e) => { if (e.target.closest('variant-picker, .product-information')) setTimeout(syncPrice, 400); });
  document.addEventListener('click', (e) => { if (e.target.closest('variant-picker, [data-sm-kit], .sm-kits')) setTimeout(syncPrice, 600); });
  window.addEventListener('popstate', () => setTimeout(syncPrice, 400));
  if (new URLSearchParams(location.search).get('variant')) syncPrice();
  const mo = new MutationObserver(() => requestAnimationFrame(apply));
  mo.observe(area, { childList: true, subtree: true });
  const stickyHost = document.querySelector('sticky-add-to-cart');
  if (stickyHost) mo.observe(stickyHost, { childList: true, subtree: true });
  apply();
}

/* ---------------------------------------------------------------- boot */
function boot() {
  priceOnButton();
  document.querySelectorAll('[data-sm-tabs]').forEach(setupTabs);
  setupAccordions();
  document.querySelectorAll('[data-sm-wall]').forEach(setupWall);
  setupScroll();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Theme editor: re-run when a section is added or reloaded.
document.addEventListener('shopify:section:load', boot);
